import React, { ChangeEvent } from 'react';
import { Avatar, Box, Button, Dialog, IconButton, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import type { ISocials } from '../../redux/contest/types';
import { profile_pic } from '../../asset/images';

const midnight = {
    primary: '#ffc965',
    primaryContainer: '#feb700',
    primaryFixed: '#392700',
    secondary: '#00fdc1',
    surface: '#0e0e0e',
    surfaceContainer: '#1a1919',
    surfaceContainerHigh: '#201f1f',
    surfaceContainerLow: '#131313',
    surfaceLowest: '#000000',
    onSurface: '#ffffff',
    onSurfaceVariant: '#adaaaa',
    outlineVariant: '#484847',
};

export interface HostPartnerFormState {
    name: string;
    description: string;
    established: string;
    website: string;
    profilePicture: File | null;
    socials: ISocials;
}

export interface ContestHostPartnerCreateModalProps {
    open: boolean;
    onClose: () => void;
    searchType: 'host' | 'partner';
    formState: HostPartnerFormState;
    profilePreview?: string;
    onFieldChange: (field: keyof HostPartnerFormState, value: string) => void;
    onImageChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onAddSocials: () => void;
    onSocialRemove: (platform: keyof ISocials) => void;
    onSave: () => void;
    saveDisabled: boolean;
}

const fieldLabelSx = {
    display: 'block',
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: midnight.onSurfaceVariant,
    mb: 1,
    ml: 0.5,
} as const;

const glassPillInputSx = {
    '& .MuiOutlinedInput-root': {
        bgcolor: 'rgba(38, 38, 38, 0.4)',
        backdropFilter: 'blur(12px)',
        borderRadius: 999,
        color: midnight.onSurface,
        fontFamily: '"Manrope", Inter, sans-serif',
        px: 1,
        py: 0.25,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '& fieldset': {
            borderColor: 'rgba(255, 201, 101, 0.05)',
        },
        '&:hover fieldset': {
            borderColor: 'rgba(255, 201, 101, 0.2)',
        },
        '&.Mui-focused fieldset': {
            borderColor: 'rgba(255, 201, 101, 0.4)',
            boxShadow: '0 0 20px rgba(255, 201, 101, 0.05)',
        },
    },
    '& .MuiInputBase-input': {
        px: 2,
        py: 1.75,
        fontSize: 15,
        '&::placeholder': {
            color: midnight.outlineVariant,
            opacity: 1,
        },
    },
};

const glassTextareaSx = {
    '& .MuiOutlinedInput-root': {
        bgcolor: 'rgba(38, 38, 38, 0.4)',
        backdropFilter: 'blur(12px)',
        borderRadius: '24px',
        color: midnight.onSurface,
        fontFamily: '"Manrope", Inter, sans-serif',
        alignItems: 'flex-start',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '& fieldset': {
            borderColor: 'rgba(255, 201, 101, 0.05)',
        },
        '&:hover fieldset': {
            borderColor: 'rgba(255, 201, 101, 0.2)',
        },
        '&.Mui-focused fieldset': {
            borderColor: 'rgba(255, 201, 101, 0.4)',
            boxShadow: '0 0 20px rgba(255, 201, 101, 0.05)',
        },
    },
    '& .MuiInputBase-inputMultiline': {
        px: 1,
        py: 1.5,
        minHeight: 96,
        maxHeight: 120,
        fontSize: 15,
        lineHeight: 1.55,
        '&::placeholder': {
            color: midnight.outlineVariant,
            opacity: 1,
        },
    },
};

function platformLabel(platform: string): string {
    return platform.replace('_', ' ').replace(/\b\w/g, char => char.toUpperCase());
}

function CreatePartnerVisualPanel({ searchType }: { searchType: 'host' | 'partner' }) {
    const accent = searchType === 'host' ? 'Host' : 'Partner';
    return (
        <Box
            sx={{
                display: { xs: 'none', md: 'block' },
                width: '33.333%',
                position: 'relative',
                overflow: 'hidden',
                flexShrink: 0,
                bgcolor: midnight.surfaceLowest,
            }}>
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background:
                        'linear-gradient(135deg, #1a1208 0%, #0e0e0e 40%, #001a14 100%), radial-gradient(ellipse 80% 60% at 30% 20%, rgba(255,201,101,0.18), transparent 55%), radial-gradient(ellipse 70% 50% at 80% 90%, rgba(0,253,193,0.08), transparent 50%)',
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.35) 45%, transparent 100%)',
                }}
            />
            <Box sx={{ position: 'absolute', bottom: 48, left: 32, right: 32, zIndex: 1 }}>
                <Typography
                    sx={{
                        fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                        fontWeight: 800,
                        fontSize: 28,
                        lineHeight: 1.2,
                        letterSpacing: '-0.02em',
                        color: midnight.onSurface,
                    }}>
                    Empower Your{' '}
                    <Box component="span" sx={{ color: midnight.primary }}>
                        Ecosystem
                    </Box>
                </Typography>
                <Typography
                    sx={{
                        color: midnight.onSurfaceVariant,
                        fontSize: 14,
                        mt: 2,
                        lineHeight: 1.6,
                        fontFamily: '"Manrope", Inter, sans-serif',
                    }}>
                    Define your campaign {accent.toLowerCase()} with precision and editorial flair.
                </Typography>
            </Box>
        </Box>
    );
}

export function ContestHostPartnerCreateModal({
    open,
    onClose,
    searchType,
    formState,
    profilePreview,
    onFieldChange,
    onImageChange,
    onAddSocials,
    onSocialRemove,
    onSave,
    saveDisabled,
}: ContestHostPartnerCreateModalProps) {
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const label = searchType === 'host' ? 'Host' : 'Partner';
    const socialEntries = Object.entries(formState.socials).filter(([, url]) => Boolean(url?.trim())) as [
        keyof ISocials,
        string,
    ][];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth={false}
            PaperProps={{
                sx: {
                    bgcolor: midnight.surfaceContainerLow,
                    backgroundImage: 'none',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    width: isMobile ? '100%' : 'min(960px, calc(100vw - 48px))',
                    maxWidth: isMobile ? '100%' : 960,
                    height: isMobile ? '100%' : 'min(795px, 92vh)',
                    maxHeight: isMobile ? '100%' : 850,
                    borderRadius: isMobile ? 0 : '40px',
                    border: isMobile ? 'none' : '1px solid rgba(255,255,255,0.05)',
                    boxShadow: isMobile ? 'none' : '0 20px 80px rgba(0,0,0,0.8)',
                    m: isMobile ? 0 : 2,
                    overflow: 'hidden',
                },
            }}>
            <CreatePartnerVisualPanel searchType={searchType} />

            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    minHeight: 0,
                    bgcolor: midnight.surfaceContainerLow,
                    overflow: 'hidden',
                }}>
                <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 3, md: 6 }, pt: { xs: 4, md: 6 }, pb: 2 }}>
                    <Box sx={{ mb: 4 }}>
                        <Typography
                            sx={{
                                fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                                fontWeight: 800,
                                fontSize: { xs: 22, md: 28 },
                                letterSpacing: '0.2em',
                                textTransform: 'uppercase',
                                color: midnight.onSurface,
                                mb: 1,
                            }}>
                            Create {label}
                        </Typography>
                        <Box sx={{ height: 4, width: 48, bgcolor: midnight.primary, borderRadius: 999 }} />
                    </Box>

                    <Box
                        component="form"
                        onSubmit={e => e.preventDefault()}
                        sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                alignItems: { xs: 'center', md: 'flex-start' },
                                gap: 4,
                            }}>
                            <Box
                                component="label"
                                sx={{
                                    position: 'relative',
                                    flexShrink: 0,
                                    cursor: 'pointer',
                                    '&:hover .avatar-ring': {
                                        borderColor: midnight.primary,
                                    },
                                }}>
                                <Box
                                    className="avatar-ring"
                                    sx={{
                                        width: 128,
                                        height: 128,
                                        borderRadius: '50%',
                                        border: '2px dashed rgba(255,201,101,0.3)',
                                        bgcolor: 'rgba(38, 38, 38, 0.4)',
                                        backdropFilter: 'blur(12px)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        transition: 'border-color 0.3s',
                                    }}>
                                    {profilePreview || formState.profilePicture ? (
                                        <Avatar
                                            src={profilePreview || profile_pic}
                                            alt="Profile"
                                            sx={{ width: '100%', height: '100%' }}
                                        />
                                    ) : (
                                        <AddAPhotoOutlinedIcon sx={{ fontSize: 40, color: '#ecaa00' }} />
                                    )}
                                </Box>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: -4,
                                        right: -4,
                                        bgcolor: midnight.primary,
                                        color: midnight.primaryFixed,
                                        p: 0.75,
                                        borderRadius: '50%',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
                                        display: 'flex',
                                    }}>
                                    <EditIcon sx={{ fontSize: 16 }} />
                                </Box>
                                <input
                                    accept="image/*"
                                    type="file"
                                    style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                                    onChange={onImageChange}
                                />
                            </Box>

                            <Box sx={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <Box>
                                    <Typography component="label" sx={fieldLabelSx}>
                                        {label} name *
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        placeholder={`e.g. Midnight ${label === 'Host' ? 'Collective' : 'Studios'}`}
                                        value={formState.name}
                                        onChange={e => onFieldChange('name', e.target.value)}
                                        sx={glassPillInputSx}
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                        gap: 2,
                                    }}>
                                    <Box>
                                        <Typography component="label" sx={fieldLabelSx}>
                                            Est. year
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            placeholder="2024"
                                            value={formState.established}
                                            onChange={e => onFieldChange('established', e.target.value)}
                                            sx={glassPillInputSx}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography component="label" sx={fieldLabelSx}>
                                            Website URL
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            placeholder="https://..."
                                            value={formState.website}
                                            onChange={e => onFieldChange('website', e.target.value)}
                                            sx={glassPillInputSx}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        <Box>
                            <Typography component="label" sx={fieldLabelSx}>
                                {label} description
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                minRows={4}
                                maxRows={4}
                                placeholder={`Describe the creative synergy and mission of this ${searchType}ship…`}
                                value={formState.description}
                                onChange={e => onFieldChange('description', e.target.value)}
                                sx={glassTextareaSx}
                            />
                        </Box>

                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography component="label" sx={{ ...fieldLabelSx, mb: 0 }}>
                                    Social links
                                </Typography>
                                <Button
                                    type="button"
                                    onClick={onAddSocials}
                                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                                    sx={{
                                        color: midnight.secondary,
                                        fontWeight: 800,
                                        fontSize: 12,
                                        letterSpacing: '0.04em',
                                        textTransform: 'none',
                                        minWidth: 0,
                                        px: 0,
                                        '&:hover': { bgcolor: 'transparent', opacity: 0.85 },
                                    }}>
                                    Add new
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {socialEntries.length ? (
                                    socialEntries.map(([platform, url]) => (
                                        <Box
                                            key={platform}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                bgcolor: midnight.surfaceContainer,
                                                borderRadius: 999,
                                                p: 0.5,
                                                pl: 2.5,
                                                transition: 'background-color 0.2s',
                                                '&:hover': { bgcolor: midnight.surfaceContainerHigh },
                                            }}>
                                            <LinkIcon
                                                sx={{ fontSize: 20, color: midnight.onSurfaceVariant, flexShrink: 0 }}
                                            />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 10,
                                                        fontWeight: 700,
                                                        letterSpacing: '0.1em',
                                                        textTransform: 'uppercase',
                                                        color: midnight.onSurfaceVariant,
                                                    }}>
                                                    {platformLabel(platform)}
                                                </Typography>
                                                <Typography
                                                    component="a"
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        display: 'block',
                                                        fontSize: 13,
                                                        color: midnight.onSurfaceVariant,
                                                        fontFamily: '"Manrope", Inter, sans-serif',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        textDecoration: 'none',
                                                        '&:hover': { color: midnight.primary },
                                                    }}>
                                                    {url}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                type="button"
                                                aria-label={`Remove ${platformLabel(platform)} link`}
                                                onClick={() => onSocialRemove(platform)}
                                                sx={{
                                                    color: 'rgba(255,115,81,0.4)',
                                                    '&:hover': { color: '#ff7351' },
                                                }}>
                                                <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                                            </IconButton>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            color: midnight.onSurfaceVariant,
                                            fontStyle: 'italic',
                                            pl: 0.5,
                                        }}>
                                        No social links yet — tap Add new to connect a profile.
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        flexShrink: 0,
                        px: { xs: 3, md: 6 },
                        py: 3,
                        borderTop: '1px solid rgba(255,255,255,0.05)',
                        display: 'flex',
                        flexDirection: { xs: 'column-reverse', md: 'row' },
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}>
                    <Button
                        type="button"
                        onClick={onClose}
                        sx={{
                            color: midnight.onSurfaceVariant,
                            fontWeight: 800,
                            fontSize: 13,
                            letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                            '&:hover': { color: midnight.onSurface, bgcolor: 'transparent' },
                        }}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        disabled={saveDisabled}
                        onClick={onSave}
                        sx={{
                            width: { xs: '100%', md: 'auto' },
                            px: 6,
                            py: 2,
                            borderRadius: 999,
                            background: `linear-gradient(to top right, ${midnight.primary}, ${midnight.primaryContainer})`,
                            color: midnight.primaryFixed,
                            fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                            fontWeight: 800,
                            fontSize: 13,
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            boxShadow: '0 0 25px rgba(255, 201, 101, 0.2)',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                                boxShadow: '0 0 35px rgba(255, 201, 101, 0.35)',
                                transform: 'scale(1.02)',
                                background: `linear-gradient(to top right, ${midnight.primary}, ${midnight.primaryContainer})`,
                            },
                            '&:active': { transform: 'scale(0.97)' },
                            '&.Mui-disabled': {
                                opacity: 0.45,
                                color: midnight.primaryFixed,
                            },
                        }}>
                        Save {label}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
}
